import * as React from 'react';
import { Bug, LoaderCircle } from 'lucide-react';
import {
  DashboardDataTable,
  DashboardDataTableColumnKind,
  DashboardTableDateCell,
  DashboardTableSearchInput,
  buildDashboardDataTableColumns,
} from '@shigo-ui/dashboard-ui';
import {
  AutocompleteFilter,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shigo-ui/ui';

import { showRequestError } from '../../../../../lib/requestErrorToast';
import { useDebouncedValue } from '../../../../../hooks/useDebouncedValue';
import { dashboardT } from '../../../../../lib/messages';
import { ShigoPermission } from '../../../../auth/permissions';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { useAdsTxtAppsQuery, useAdsTxtMutations, useAdsTxtPublishersQuery } from '../../hooks';
import {
  adsTxtPageSizes,
  AdsTxtAppsFoundCriteria,
  hasAdsTxtCrawlDateChanged,
  type AdsTxtApp,
} from '../../model';
import { useAdsTxtAppsRouteFilters } from '../../useAdsTxtAppsRouteFilters';
import { AdsTxtAppResult, AdsTxtAppTooltipCell } from './AdsTxtAppsTableCells';
import { formatDisplayDate } from '../../../../../lib/format';

interface CrawlTarget {
  id: string;
  location: string;
  previousDate: string | null | undefined;
}

export function AdsTxtAppsTable() {
  const { can } = useShigoPermissions();
  const routeFilters = useAdsTxtAppsRouteFilters();
  const { filter } = routeFilters;
  const query = useAdsTxtAppsQuery(filter);
  const { refetch } = query;
  const publishers = useAdsTxtPublishersQuery();
  const mutations = useAdsTxtMutations();
  const search = useDebouncedValue(filter.searchTerm, routeFilters.setSearch, 650);
  const [crawlTarget, setCrawlTarget] = React.useState<CrawlTarget | null>(null);

  React.useEffect(() => {
    if (!crawlTarget) return;
    let attempts = 0;
    const timer = window.setInterval(() => {
      void refetch().then((result) => {
        attempts += 1;
        const completed = result.data?.content.some(
          (app) =>
            app.adsTxtLocation === crawlTarget.location &&
            hasAdsTxtCrawlDateChanged(crawlTarget.previousDate, app.adstxtCrawledOn),
        );
        if (completed || attempts >= 30) setCrawlTarget(null);
      });
    }, 2_000);
    return () => {
      window.clearInterval(timer);
    };
  }, [crawlTarget, refetch]);

  const columns = buildDashboardDataTableColumns<AdsTxtApp>([
    textColumn('appStoreId', 'adsTxt.apps.appStoreId', (app) => app.appStoreId ?? '—', 'w-40'),
    textColumn('platform', 'adsTxt.apps.platform', (app) => app.platform?.name ?? '—', 'w-36'),
    textColumn(
      'appStoreUrl',
      'adsTxt.apps.appStoreUrl',
      (app) => <AdsTxtAppTooltipCell value={app.appStoreUrl} />,
      'w-64',
    ),
    textColumn(
      'publishers',
      'adsTxt.apps.publishers',
      (app) => <AdsTxtAppTooltipCell value={app.publisherNames} />,
      'w-52',
    ),
    textColumn('adsTxtUrl', 'adsTxt.apps.adsTxtUrl', (app) => app.adsTxtLocation ?? '—', 'w-56'),
    {
      id: 'result',
      kind: DashboardDataTableColumnKind.Custom,
      header: dashboardT('adsTxt.apps.result'),
      headerClassName: 'w-28 text-center',
      className: 'w-28 text-center',
      cell: (app) => <AdsTxtAppResult app={app} />,
    },
    {
      id: 'crawlDate',
      kind: DashboardDataTableColumnKind.Custom,
      header: dashboardT('adsTxt.crawlDate'),
      headerClassName: 'w-44',
      className: 'w-44',
      cell: (app) => (
        <DashboardTableDateCell>
          {formatDisplayDate(app.adstxtCrawledOn, {
            fallback: '—',
            keepInvalid: true,
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </DashboardTableDateCell>
      ),
    },
    {
      id: 'crawl',
      kind: DashboardDataTableColumnKind.Actions,
      header: dashboardT('adsTxt.crawl'),
      headerClassName: 'w-20 text-center',
      className: 'w-20 text-center',
      cell: (app) =>
        can(ShigoPermission.SupplyAdsTxtAppsCrawl) ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={!app.adsTxtLocation || Boolean(crawlTarget)}
                  aria-label={dashboardT('adsTxt.apps.crawlApp', { app: app.appStoreId ?? app.id })}
                  onClick={() => void startCrawl(app)}
                >
                  {crawlTarget?.id === app.id ? (
                    <LoaderCircle className="h-4 w-4 animate-spin" />
                  ) : (
                    <Bug className="h-4 w-4" />
                  )}
                </Button>
              </span>
            </TooltipTrigger>
            {!app.adsTxtLocation ? (
              <TooltipContent>{dashboardT('adsTxt.apps.missingLocation')}</TooltipContent>
            ) : null}
          </Tooltip>
        ) : null,
    },
  ]);

  async function startCrawl(app: AdsTxtApp) {
    if (!app.adsTxtLocation) return;
    setCrawlTarget({
      id: app.id,
      location: app.adsTxtLocation,
      previousDate: app.adstxtCrawledOn,
    });
    try {
      await mutations.crawlApp.mutateAsync(app.id);
    } catch (error) {
      setCrawlTarget(null);
      showRequestError(error, dashboardT('adsTxt.apps.crawlError'));
    }
  }

  return (
    <main className="page-gutter min-w-0 flex-1 py-4 md:py-6">
      <DashboardDataTable
        title={dashboardT('adsTxt.apps.title')}
        tableLabel={dashboardT('adsTxt.apps.title')}
        columns={columns}
        items={query.data?.content ?? []}
        getRowKey={(app) => app.id}
        loading={query.isLoading}
        loadingMessage={dashboardT('adsTxt.apps.loading')}
        errorMessage={query.isError ? dashboardT('adsTxt.apps.loadError') : undefined}
        emptyMessage={dashboardT('adsTxt.apps.empty')}
        minWidthClassName="min-w-[1320px]"
        toolbar={
          <>
            <DashboardTableSearchInput
              aria-label={dashboardT('adsTxt.apps.search')}
              placeholder={dashboardT('adsTxt.apps.search')}
              value={search.draft}
              onChange={(event) => {
                search.setDraft(event.target.value);
              }}
            />
            <AutocompleteFilter
              label={dashboardT('adsTxt.apps.publisherFilter')}
              options={(publishers.data?.content ?? []).map((publisher) => ({
                id: publisher.id,
                label: publisher.name ?? publisher.displayName ?? publisher.id,
              }))}
              selectedIds={filter.publisherIds}
              onSelectedIdsChange={routeFilters.setPublishers}
              loading={publishers.isLoading}
              searchPlaceholder={dashboardT('adsTxt.apps.publisherSearch')}
              className="w-[260px] max-w-full"
            />
            <Select
              value={filter.foundCriteria}
              onValueChange={(value: AdsTxtAppsFoundCriteria) => {
                routeFilters.setFoundCriteria(value);
              }}
            >
              <SelectTrigger
                className="bg-card w-[220px] max-w-full"
                aria-label={dashboardT('adsTxt.apps.criteriaFilter')}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.values(AdsTxtAppsFoundCriteria).map((criteria) => (
                  <SelectItem key={criteria} value={criteria}>
                    {dashboardT(`adsTxt.apps.criteria.${criteria}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        }
        pagination={{
          value: {
            page: filter.page,
            pageSize: filter.size,
            totalItems: query.data?.totalElements ?? 0,
          },
          pageSizes: adsTxtPageSizes,
          disabled: query.isFetching,
          onChange: (next) => {
            routeFilters.setPagination(next.page, next.pageSize);
          },
        }}
      />
    </main>
  );
}

function textColumn(
  id: string,
  key: Parameters<typeof dashboardT>[0],
  value: (app: AdsTxtApp) => React.ReactNode,
  width: string,
) {
  return {
    id,
    kind: DashboardDataTableColumnKind.Text as const,
    header: dashboardT(key),
    headerClassName: width,
    className: `${width} truncate`,
    value,
  };
}
